import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useInjectClientData } from "@hooks/useInjectClientData";
import Attachment from "@particles/card/templates/TemplateEv2/components/Attachment";
import TemplateEv2 from "@particles/card/templates/TemplateEv2/TemplateEv2";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import LeadReport from "@routes/Keyword/templates/AseanInsights/components/LeadReport";
import { CampaignTemplate3Data } from "@routes/Keyword/utils/types";
import { sectionNavigationItems } from "@util/constant";

/**
 * This is the Campaign Template 3 - Asean Insights template.
 * @figma https://www.figma.com/design/9RrH1KYIUOZk6rvgzlBBKX/WEB-BAU-2026?node-id=20436-71260&t=U0FzaVLIVAyLLNk0-4
 * */
const AseanInsights = ({
  entity,
  articles,
  seoFooterData,
  gaProps,
}: CampaignTemplate3Data & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const { tag, attachments } = entity;
  const absolutePath = tag.urlPath;
  const relativePath = getUrlPathFromAbsoluteUrl(absolutePath);
  const attachmentUrl = attachments?.[0]?.urlPath;

  return (
    <div
      className="w-full"
      data-testid="campaign-template-asean-insights-component"
    >
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        parentCategory="keywords"
        childCategory={relativePath.replace("/keywords/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      {attachmentUrl ? (
        <LeadReport className="mb-10" attachmentURL={attachmentUrl} />
      ) : null}

      <Container rootClassName="px-6">
        <TemplateEv2
          cardProps={articles}
          className="mb-9"
          header={
            <BlockTitle
              text="Report Highlights"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center mb-7"
            />
          }
          lastComponent={
            <>
              {attachmentUrl ? (
                <Attachment
                  className="col-span-4 xl:col-span-3"
                  attachmentURL={attachmentUrl}
                />
              ) : null}
            </>
          }
        />

        <p className="font-poppins text-base font-normal text-gray-850">
          Read more about Asia and beyond at The Business Times Global. Visit us
          at{" "}
          <Link
            to={`${RouteFactory.global}?reroute=no`}
            target="_blank"
            className="font-medium text-blue-175 underline hover:text-blue-300"
          >
            bt.global
          </Link>
          .
        </p>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="prestitial" />
      <Ad adType="catfish" />
    </div>
  );
};

export default AseanInsights;
